import React, { ReactNode } from 'react';
import classNames from 'classnames';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';

import { Modal } from 'ui/base/modal';
import { ButtonClickEvent, FormSubmitEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';

import { ActiveButton, DefaultButton } from 'ui/public/button';
import { LabeledInput, PasswordInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { TIMEZONE_NAME_MAP } from 'ui/standard/input/timezone_input/timezone_data';

import { ResultKind } from 'services/base/types';
import { BasePath } from 'services/base/page_path';
import { HistoryService } from 'services/base/history';
import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { IPangolinDomain } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import { UnlinkPangolinStore } from './unlink_pangolin_store';
import baseStyles from './unlink_pangolin.module.css';
import Messages from './unlink_pangolin_messages';

type UnlinkPangolinStyles = {
  iconContainer: string;
  textDetail: string;
  deviceTimezone: string;
  buttonContainer: string;
  label: string;
  content: string;
  versionText: string;
  modalHeaderRow: string;
  modalContainer: string;
  modalContainerPopOut: string;
  closeIcon: string;
  warningText: string;
  rebootContainer: string;
  unlinkContainer: string;
};

const themedStyles: ThemedStyles<UnlinkPangolinStyles> = {
  [Theme.DAYLIGHT]: {
    iconContainer: baseStyles.baseVerticalSpace,
    textDetail: baseStyles.textDetail,
    deviceTimezone: baseStyles.deviceTimezone,
    buttonContainer: baseStyles.buttonContainer,
    label: baseStyles.daylightLabel,
    content: baseStyles.content,
    versionText: baseStyles.versionText,
    modalHeaderRow: baseStyles.passwordModalHeaderRow,
    modalContainer: baseStyles.passwordModalContainer,
    modalContainerPopOut: baseStyles.passwordModalContainerPopOut,
    closeIcon: baseStyles.daylightCloseIcon,
    warningText: baseStyles.warningText,
    rebootContainer: baseStyles.daylightRebootContainer,
    unlinkContainer: baseStyles.unlinkContainer,
  },
  [Theme.MIDNIGHT]: {
    iconContainer: baseStyles.baseVerticalSpace,
    textDetail: baseStyles.textDetail,
    deviceTimezone: baseStyles.deviceTimezone,
    buttonContainer: baseStyles.buttonContainer,
    label: baseStyles.midnightLabel,
    content: baseStyles.content,
    versionText: baseStyles.versionText,
    modalHeaderRow: baseStyles.passwordModalHeaderRow,
    modalContainer: baseStyles.passwordModalContainer,
    modalContainerPopOut: baseStyles.passwordModalContainerPopOut,
    closeIcon: baseStyles.midnightCloseIcon,
    warningText: baseStyles.warningText,
    rebootContainer: baseStyles.midnightRebootContainer,
    unlinkContainer: baseStyles.unlinkContainer,
  },
};

type BaseUnlinkPangolinProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
  domain: IPangolinDomain;
};

export const BaseUnlinkPangolin = observer(({ accounts, pangolin, domain }: BaseUnlinkPangolinProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new UnlinkPangolinStore(pangolin));
  React.useEffect(() => { setStore(new UnlinkPangolinStore(pangolin)); }, [pangolin]);

  const onModalClose = React.useCallback((event: ButtonClickEvent) => {
    event.preventDefault();
    store.passwordModal.hideModal();
  }, [store]);

  const askForPassword = React.useCallback((event: FormSubmitEvent) => {
    event.preventDefault();
    store.passwordModal.showModal();
  }, [store]);

  const handleReboot = React.useCallback((event: FormSubmitEvent) => {
    event.preventDefault();
    runInAction(async() => {
      await store.busyReboot.perform(() => store.handleReboot());
    });
  }, [store]);

  const handleUnlink = React.useCallback((event: FormSubmitEvent) => {
    event.preventDefault();
    runInAction(async() => {
      const result = await store.busyUnlink.perform(() => store.handleUnlink(accounts));

      if (result.kind === ResultKind.OK) {
        if (accounts.pangolins.size > 0) {
          // go back up two pages
          history.goBack();
          history.goBack();
        } else {
          // all pangolins have been unlinked, so redirect to the add pangolin page again
          await HistoryService.push(history, { kind: BasePath.ADD_PANGOLIN_SETUP });
        }
      }
    });
  }, [store, accounts, history]);

  let updateSnippet: ReactNode = null;

  const updateCurrentRelease = (
    !pangolin.updateActiveReleaseID ? undefined :
    domain.updateReleases.get(pangolin.updateActiveReleaseID)
  );

  const updateCurrentTag = updateCurrentRelease?.tag ?? pangolin.updateVersion;

  if (updateCurrentTag) {
    updateSnippet = (
      <div className={styles.textDetail}>
        <div className={styles.label}>{Messages.firmwareVersion()}</div>
        <div className={styles.versionText}>
          {updateCurrentTag}
        </div>
      </div>
    );
  }

  // future: separate modal into its own component? with an onSubmit link
  return (
    <StandardPage>
      <Modal store={store.passwordModal}>
        <div className={classNames(styles.modalContainer, { [styles.modalContainerPopOut]: store.passwordModal.visible })}>
          <StandardPage>
            <div className={styles.modalHeaderRow}>
              {Messages.modalTitle()}
              <button className={styles.closeIcon} onClick={onModalClose}/>
            </div>
            <div className={styles.warningText}>
              {Messages.unlinkWarning()}
            </div>
            <form onSubmit={handleUnlink}>
              <LabeledInput label={Messages.password()}>
                <PasswordInput store={store.password} id='password' name='password'/>
              </LabeledInput>
              <ErrorMessage message={store.idError}/>
              <ErrorMessage message={store.passwordError}/>
              <ErrorMessage message={store.updateUpgradeError}/>
              <ErrorMessage message={store.updateRestoreError}/>
              <ErrorMessage message={store.planFirewallSubscriptionError}/>
              <div className={styles.buttonContainer}>
                <ActiveButton busy={store.busyUnlink} type='submit'>{Messages.unlinkPangolin()}</ActiveButton>
              </div>
            </form>
          </StandardPage>
        </div>
      </Modal>
      <BackButton/>
      <div className={styles.iconContainer}>
        <ShieldIcon colorCode={store.pangolin.colorCode} size={ShieldSize.EXTRA_LARGE}/>
      </div>
      <div className={styles.textDetail}>
        <div className={styles.label}>{Messages.pangolinName()}</div>
        <div className={styles.content}>{store.pangolin.nickname}</div>
      </div>
      <div className={styles.deviceTimezone}>
        <div className={styles.label}>{Messages.deviceTimezone()}</div>
        <div className={styles.content}>
          {`${TIMEZONE_NAME_MAP[store.pangolin.timezone].label} (${TIMEZONE_NAME_MAP[store.pangolin.timezone].offsetString})`}
        </div>
      </div>
      {updateSnippet}
      <div className={styles.rebootContainer}>
        <form onSubmit={handleReboot}>
          <div className={styles.buttonContainer}>
            <DefaultButton busy={store.busyReboot} type='submit'>{Messages.rebootPangolin()}</DefaultButton>
            <ErrorMessage message={store.rebootError}/>
          </div>
        </form>
      </div>
      <div className={styles.unlinkContainer}>
        <form onSubmit={askForPassword}>
          <div className={styles.buttonContainer}>
            <ActiveButton type='submit'>{Messages.unlinkPangolin()}</ActiveButton>
          </div>
        </form>
      </div>
    </StandardPage>
  );
});

export const UnlinkPangolin = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolinID);
  if (!domain) return <Redirect />;

  return (
    <BaseUnlinkPangolin
      accounts={accounts}
      pangolin={pangolin}
      domain={domain}
    />
  );
});
